/**
 * @author       esraa.afifi@zumper.com (Esraa Afifi)
 * @copyright    Copyright (c) 2021, Zumper
 * @description  styles.ts
 */

import { type Theme, css } from '@emotion/react'
import { ALPHA_50, BREAKPOINT_DESKTOP, Z_INDEX_GROUP_2, Z_INDEX_GROUP_3 } from '@zcode-ui/themes'

import { darkBlue, gray70 } from '../../../constants/styles'

const FONT_FAMILY_BASE = 'Moderat, Arial, sans-serif'

export const hover = ({ colors }: Theme) => css`
  z-index: ${Z_INDEX_GROUP_3};
  @media (min-width: ${BREAKPOINT_DESKTOP}) {
    .rentableMarkerContent {
      background: ${colors?.secondaryHover ?? darkBlue};

      box-shadow: 0 1px 4px ${colors.textLight}${ALPHA_50};
    }
  }
`

export const visited = ({ colors }: Theme) => css`
  .rentableMarkerContent {
    background-color: ${colors?.accentLight ?? gray70};
    box-shadow: 0 1px 4px ${colors.textLight}${ALPHA_50};
  }
`

export const hoveredVerticalBar = ({ colors }: Theme) => css`
  z-index: ${Z_INDEX_GROUP_3};
  background: ${colors?.primary ?? '#E84676'};
  box-shadow: 0 1px 4px ${colors.textLight}${ALPHA_50};
`

export const visitedVerticalBar = ({ colors }: Theme) => css`
  background: ${colors?.accentLight ?? gray70};
  box-shadow: 0 1px 4px ${colors.textLight}${ALPHA_50};
`

export const hoveredCircle = ({ colors }: Theme) => css`
  background: ${colors?.primary ?? '#E84676'};
`

export const visitedCircle = ({ colors }: Theme) => css`
  background: ${colors?.accentLight ?? gray70};
`

export const verticalBar = ({ colors }: Theme) => css`
  position: absolute;
  left: 50%;
  z-index: (${Z_INDEX_GROUP_3} + 1);

  width: 2px;
  height: 5px;

  box-shadow: 0 1px 4px ${colors.textLight}${ALPHA_50};
  transform: translateX(-50%);

  label: verticalBar;
`

export const circle = ({ colors }: Theme) => css`
  position: absolute;
  bottom: 0;
  left: 50%;

  width: 4px;
  height: 4px;

  border-radius: 50%;
  transform: translateX(-50%);

  label: circle;
`

export const rentableMarkerContent = ({ colors, typography }: Theme) => css`
  position: relative;

  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px;

  color: #fff;
  font-weight: ${typography?.fontWeightBold ?? 'bold'};
  font-size: 14px;
  font-family: ${FONT_FAMILY_BASE};
  line-height: 1em;
  text-align: center;

  border-radius: 2px;
  box-shadow: 0 1px 4px ${colors.textLight}${ALPHA_50};
  cursor: pointer;

  user-select: none;

  :hover {
    background: ${colors?.primary ?? '#E84676'};

    box-shadow: 0 1px 4px ${colors.textLight}${ALPHA_50};

    .css-${verticalBar.name} {
      background: ${colors?.secondaryHover ?? darkBlue};
      box-shadow: 0 1px 4px ${colors.textLight}${ALPHA_50};
    }
    .css-${circle.name} {
      background: ${colors?.secondaryHover ?? darkBlue};
    }
  }
`

export const rentableMarkerArrow = css`
  position: relative;

  display: flex;
  justify-content: center;
  height: 9px;

  pointer-events: none;
`

export const text = ({ typography }: Theme) => css`
  font-weight: ${typography?.fontWeightBold ?? 'bold'};
`

export const hoveredRentableMarkerContent = ({ colors }: Theme) => css`
  background: ${colors?.primary ?? '#E84676'};

  box-shadow: 0 1px 4px ${colors.textLight}${ALPHA_50};
`

export const visitedRentableMarkerContent = ({ colors }: Theme) => css`
  box-shadow: 0 1px 4px ${colors.textLight}${ALPHA_50};
`

export const rentableMarker = css`
  z-index: ${Z_INDEX_GROUP_2};

  &:hover {
    z-index: ${Z_INDEX_GROUP_3};
  }

  &:active {
    z-index: ${Z_INDEX_GROUP_3};
  }
`
